import * as React from "react";

import { styled } from "@mui/material/styles";
import { Drawer, Stack } from "@mui/material";

import { NotesPanel } from "../../../components/meeting";
import { WebLayout } from "../../../components/meeting/layouts";
import { MeetingToolbar, NotesToggleButton } from "../../../components/meeting/meeting-toolbar";

/**
 * Constants
 */
// Width of the notes drawer as a viewport width percentage
const DRAWER_WIDTH = "20vw";
// AppBar height in pixels (standard Material-UI desktop height)
const APP_BAR_HEIGHT = 64;
// Meeting view height percentage when controls are visible
const MEETING_VIEW_HEIGHT = 92;
// Meeting view width percentages
const MEETING_VIEW_WIDTH_OPEN = 85; // When notes panel is open
const MEETING_VIEW_WIDTH_CLOSED = 100; // When notes panel is closed

/**
 * Props interface for the SizeChangeStack styled component
 */
interface SizeChangeStackProps {
  open: boolean;
}

/**
 * Styled Stack component that adjusts its size based on the notes panel state.
 * When the notes panel is open, it reduces the width and adds a right margin
 * to accommodate the drawer. Includes smooth transitions for size changes.
 */
const SizeChangeStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "open",
})<SizeChangeStackProps>(({ theme, open }) => ({
  alignItems: "center",
  height: "80vh",
  width: open ? undefined : "100%",
  // Transition configuration for closing the drawer
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  // Styles applied when the drawer is open
  ...(open && {
    width: `calc(100vw - ${DRAWER_WIDTH})`,
    marginRight: `${DRAWER_WIDTH}`,
    // Transition configuration for opening the drawer
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));


const MetroLayout: React.FC = () => {

  // State to control the visibility of the notes panel
  const [isOpen, setIsOpen] = React.useState(false);

  // Dynamic view dimensions based on notes panel state
  const viewHeight = MEETING_VIEW_HEIGHT;
  const viewWidth = isOpen ? MEETING_VIEW_WIDTH_OPEN : MEETING_VIEW_WIDTH_CLOSED;

  /**
   * Toggles the visibility of the notes drawer panel
   */
  const toggleDrawer = () => {
    setIsOpen(!isOpen);
  };

  /**
   * Prevents event propagation and default behavior for mouse events.
   * This ensures that meeting controls don't interfere with the underlying
   * meeting interface interactions.
   */
  const stopEvents = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <>
      {/* Main container - full screen with black background */}
      <Stack
        height="100vh"
        width="100vw"
        onClick={stopEvents}
        onMouseDown={stopEvents}
        onMouseUp={stopEvents}
        onContextMenu={stopEvents}
        sx={{
          display: "flex",
          backgroundColor: "#000000",
        }}
      >
        {/* Top navigation bar with meeting controls */}
        <MeetingToolbar>
          {/* Notes toggle button with dynamic icon and tooltip */}
          <NotesToggleButton isOpen={isOpen} onToggle={toggleDrawer} />
        </MeetingToolbar>

        {/* Main content area that resizes based on notes panel state */}
        <SizeChangeStack open={isOpen} id="size-change-stack">
          {/* Persistent drawer for notes panel */}
          <Drawer
            sx={{
              width: "fit-content",
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: DRAWER_WIDTH,
                boxSizing: "border-box",
                top: `${APP_BAR_HEIGHT}px`, // Position below the AppBar
                height: `calc(100vh - ${APP_BAR_HEIGHT}px)`, // Adjust height to account for AppBar
                backgroundColor: "#333",
              },
            }}
            variant="persistent"
            anchor="right"
            open={isOpen}
          >
            <NotesPanel />
          </Drawer>

          {/* Main meeting component */}
          <WebLayout
            viewHeight={viewHeight}
            viewWidth={viewWidth}
            layout="metro"
          />
        </SizeChangeStack>
      </Stack>
    </>
  );
};

export default MetroLayout;
